import {
  TabContainer,
  TabButton,
  TabIcon,
  TabImage,
  ActiveIndicator,
} from "../TenderProcessTracker/TenderProcessTracker.styles";
import { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { resetTenderToFinalizedSummary, resetTenderToProductComparison, reimportSourceDocuments } from "../../api/api";
import { useToast } from "../ToastProvider/ToastProvider";

const tabs = [
  "Tender Initialization",
  "Source Documents",
  "Finalized Summary",
  "Product Comparison",
  "Client Comparison",
];

export default function TenderProcessTracker() {
  const location = useLocation();
  const navigate = useNavigate();
  const { tenderId } = useParams();
  const { showToast } = useToast();
  const pathSegments = location.pathname.split("/").filter(Boolean);

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasUserHoveredFinalizedSummary, setHasUserHoveredFinalizedSummary] = useState(false);

  const [openProductComparison, setOpenProductComparison] = useState(false);
  const [loadingProductComparison, setLoadingProductComparison] = useState(false);
  const [hasUserHoveredProductComparison, setHasUserHoveredProductComparison] = useState(false);

  const [openSourceDocs, setOpenSourceDocs] = useState(false);
  const [loadingSourceDocs, setLoadingSourceDocs] = useState(false);
  const [hasUserHoveredSourceDocs, setHasUserHoveredSourceDocs] = useState(false);

  function kebabToTitleCase(str) {
    return str
      .split("-")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  }

  const breadcrumbToTabMapping = {
    "New Tender": "Tender Initialization",
  };

  const filteredSegments = pathSegments
    .filter((seg) => isNaN(seg))
    .map(kebabToTitleCase);

  const resolvedBreadcrumb =
    breadcrumbToTabMapping[filteredSegments[filteredSegments.length - 1]] ||
    tabs.find((tab) =>
      filteredSegments.includes(tab)
    ) ||
    tabs[0];

  const [activeTab, setActiveTab] = useState(tabs[0]);

  useEffect(() => {
    const matchedTab = tabs.find((tab) => tab === resolvedBreadcrumb);
    if (matchedTab) {
      setActiveTab(matchedTab);
    }
  }, [resolvedBreadcrumb]);

  const isSourceDocsClickable = (tab, activeTab) => {
    if (tab !== "Source Documents") return false;
    if (tabs.indexOf(activeTab) > tabs.indexOf("Source Documents")) return true;
    return activeTab === "Source Documents" && filteredSegments.includes("Generate");
  };

  const isDisabled = (tab, activeTab) => {
    if (tab === "Product Comparison") return activeTab !== "Client Comparison";
    if (tab === "Finalized Summary")  return activeTab !== "Product Comparison" && activeTab !== "Client Comparison";
    if (tab === "Source Documents")   return !isSourceDocsClickable(tab, activeTab);
    return true;
  };

  const isClickable = (tab, activeTab) => {
    if (tab === "Product Comparison") return activeTab === "Client Comparison";
    if (tab === "Finalized Summary")  return activeTab === "Product Comparison" || activeTab === "Client Comparison";
    return isSourceDocsClickable(tab, activeTab);
  };

  // Product Comparison handlers
  const handleProductComparisonClickOpen = () => setOpenProductComparison(true);
  const handleProductComparisonClose = () => !loadingProductComparison && setOpenProductComparison(false);

  const handleProductComparisonConfirm = async () => {
    setLoadingProductComparison(true);
    try {
      await resetTenderToProductComparison(tenderId);
      setOpenProductComparison(false);
      navigate(`/${tenderId}/product-comparison/upload`);
    } catch (error) {
      if (error.isAuthExpired) return;
      showToast("Failed to move back to Product Comparison. Please let us know along with the Quote ID.", "error");
    } finally {
      setLoadingProductComparison(false);
    }
  };

  const confirmBackToProductComparison = () => {
    if (activeTab === "Client Comparison") handleProductComparisonClickOpen();
  };

  // Finalized Summary handlers
  const handleClickOpen = () => setOpen(true);
  const handleClose = () => !loading && setOpen(false);

  const handleConfirm = async () => {
    setLoading(true);
    try {
      await resetTenderToFinalizedSummary(tenderId);
      setOpen(false);
      navigate(`/${tenderId}/finalized-summary/upload`);
    } catch (error) {
      if (error.isAuthExpired) return;
      showToast("Failed to move back to Finalised Summary. Please let us know along with the Quote ID.", "error");
    } finally {
      setLoading(false);
    }
  };

  const confirmBackToPdf = () => {
    if (tabs.indexOf(activeTab) > tabs.indexOf("Finalized Summary")) {
      handleClickOpen();
    }
  };

  // Source Documents handlers
  const handleSourceDocsClickOpen = () => setOpenSourceDocs(true);
  const handleSourceDocsClose = () => !loadingSourceDocs && setOpenSourceDocs(false);

  const handleSourceDocsConfirm = async () => {
    setLoadingSourceDocs(true);
    try {
      const data = await reimportSourceDocuments(tenderId);
      setOpenSourceDocs(false);
      navigate(`/${tenderId}/source-documents/upload`, { state: { existingFiles: data.files } });
    } catch (error) {
      if (error.isAuthExpired) return;
      showToast("Failed to move back to Source Documents. Please let us know along with the Quote ID.", "error");
    } finally {
      setLoadingSourceDocs(false);
    }
  };

  const confirmBackToSourceDocs = () => {
    if (isSourceDocsClickable("Source Documents", activeTab)) {
      handleSourceDocsClickOpen();
    }
  };

  return (
    <>
      <TabContainer data-testid="tender-progress-indicator" role="tablist">
        {tabs.map((tab) => (
          <TabButton
            key={tab}
            active={activeTab === tab}
            isFinalizedSummary={tab === "Finalized Summary"}
            isProductComparison={tab === "Product Comparison"}
            isSourceDocuments={tab === "Source Documents"}
            isClickable={isClickable(tab, activeTab)}
            hasUserHovered={
              tab === "Finalized Summary"  ? hasUserHoveredFinalizedSummary :
              tab === "Product Comparison" ? hasUserHoveredProductComparison :
              tab === "Source Documents"   ? hasUserHoveredSourceDocs : false
            }
            aria-label={tab}
            role="tab"
            aria-selected={activeTab === tab}
            data-testid="tender-step-active"
            disabled={isDisabled(tab, activeTab)}
            onClick={() => {
              if (tab === "Product Comparison") confirmBackToProductComparison();
              if (tab === "Finalized Summary")  confirmBackToPdf();
              if (tab === "Source Documents")   confirmBackToSourceDocs();
            }}
            onMouseEnter={
              tab === "Finalized Summary"  ? () => setHasUserHoveredFinalizedSummary(true) :
              tab === "Product Comparison" ? () => setHasUserHoveredProductComparison(true) :
              tab === "Source Documents"   ? () => setHasUserHoveredSourceDocs(true) : undefined
            }
          >
            <TabIcon active={activeTab === tab}>
              <TabImage
                src={
                  activeTab === tab
                    ? "/Icons/tender-process-active.svg"
                    : "/Icons/tender-process-inactive.svg"
                }
                alt={tab}
              />
            </TabIcon>
            <span>{tab}</span>
            <ActiveIndicator active={activeTab === tab} />
          </TabButton>
        ))}
      </TabContainer>

      <Dialog open={open} onClose={handleClose} data-testid="reset-dialog">
        <DialogTitle>Move back to Finalised Summary?</DialogTitle>
        <DialogContent>
          <DialogContentText data-testid="reset-dialog-content">
            Moving back will clear all files on SharePoint from Finalised Summary import 
            up until where you are now. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            disabled={loading}
            sx={{ color: 'black' }}
            data-testid="cancel-reset"
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirm}
            color="primary"
            disabled={loading}
            data-testid="confirm-reset"
          >
            {loading ? <CircularProgress size={20} /> : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openProductComparison} onClose={handleProductComparisonClose} data-testid="reset-product-comparison-dialog">
        <DialogTitle>Move back to Product Comparison?</DialogTitle>
        <DialogContent>
          <DialogContentText data-testid="reset-product-comparison-dialog-content">
            Moving back will clear all files on SharePoint from Product Comparison
            up until where you are now. Are you sure you want to continue?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleProductComparisonClose}
            disabled={loadingProductComparison}
            sx={{ color: 'black' }}
            data-testid="cancel-reset-product-comparison"
          >
            Cancel
          </Button>
          <Button
            onClick={handleProductComparisonConfirm}
            color="primary"
            disabled={loadingProductComparison}
            data-testid="confirm-reset-product-comparison"
          >
            {loadingProductComparison ? <CircularProgress size={20} /> : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openSourceDocs} onClose={handleSourceDocsClose} data-testid="reset-source-docs-dialog">
        <DialogTitle>Move back to Source Documents Import?</DialogTitle>
        <DialogContent>
          <DialogContentText data-testid="reset-source-docs-dialog-content">
            Moving back will clear all the progress on this quote, retaining only the initially imported source documents.<br></br><br></br>
            This will provide you the opportunity to restart the quote while swapping out some insurer documents.<br></br><br></br>
            Are you sure you wish to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleSourceDocsClose}
            disabled={loadingSourceDocs}
            sx={{ color: 'black' }}
            data-testid="cancel-reset-source-docs"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSourceDocsConfirm}
            color="primary"
            disabled={loadingSourceDocs}
            data-testid="confirm-reset-source-docs"
          >
            {loadingSourceDocs ? <CircularProgress size={20} /> : "Continue"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
